import { AfterViewInit, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NotificationService } from 'app/_services/notification.service';
import { UserValidation } from 'app/general/_models/user-validation-model';
import { UserValidationService } from '../../user-validation.service';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { CELLPHONE_MASK, PHONE_ALTERNATIVE_MASK } from 'app/_utils/general-masks';

declare const $: any;

@Component({
    selector: 'app-user-data-validation', templateUrl: './user-data-validation.component.html', styleUrls: ['./user-data-validation.component.scss'],
    standalone: false
})
export class UserDataValidationComponent implements OnInit, AfterViewInit {
    @Input() user: UserValidation;
    @Output() onNextStepClick: EventEmitter<void> = new EventEmitter<void>();

    firstNameControl: FormControl<string>;
    lastNameControl: FormControl<string>;
    phoneControl: FormControl<string>;

    fb = inject(FormBuilder);
    userInfoFormGroup = this.fb.group({
        firstName: ["", [Validators.required]],
        lastName: ["", [Validators.required]],
        phone: ["", [Validators.required]],
    });

    validationMessages: { firstName: any, lastName: any, phone: any };

    phoneMask: string = PHONE_ALTERNATIVE_MASK;

    constructor(private userValidationService: UserValidationService, private notificationService: NotificationService, private ngxLoader: NgxUiLoaderService) {}

    ngOnInit(): void {
        this.userInfoFormGroup.patchValue({
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            phone: this.user.phone,
        })

        this.firstNameControl = this.userInfoFormGroup.controls.firstName as FormControl;
        this.lastNameControl = this.userInfoFormGroup.controls.lastName as FormControl;
        this.phoneControl = this.userInfoFormGroup.controls.phone as FormControl;

        this.phoneControl.valueChanges.subscribe((value) => {
            if (value.length == 0)
                return;

            if (value.length == 10)
                this.phoneControl.setErrors(null);

            this.phoneMask = value.length > 10 ? CELLPHONE_MASK : PHONE_ALTERNATIVE_MASK;
        });

        this.validationMessages = {
            firstName: {
                required: 'geral.user-validation.general-data.form.validation-message.first-name.required',
            },
            lastName: {
                required: 'geral.user-validation.general-data.form.validation-message.last-name.required',
            },
            phone: {
                required: 'geral.user-validation.general-data.form.validation-message.phone.required',
                mask: 'geral.user-validation.general-data.form.validation-message.phone.mask'
            }
        };
    }

    ngAfterViewInit() {
        this.phoneControl.setErrors(null);
    }

    nextStep(): void {
        if (!this.userDataHasChanged())
            return this.onNextStepClick.emit();

        if (!this.isFormValid())
            return;

        this.ngxLoader.startLoader('loader-user-validation');

        var firstName = this.firstNameControl.value;
        var lastName = this.lastNameControl.value;
        var phone = this.phoneControl.value;

        this.userValidationService.updateUserData(firstName, lastName, phone).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                this.notificationService.showSuccessToastr('geral.user-validation.general-data.messages.success.submit', { translateMessage: true });

                this.user.firstName = firstName;
                this.user.lastName = lastName;
                this.user.phone = phone;

                this.onNextStepClick.emit();
                this.ngxLoader.stopLoader('loader-user-validation');
            },
            error: (error) => {
                this.notificationService.showErrorToastr('geral.user-validation.general-data.messages.error.submit', { translateMessage: true });
                this.ngxLoader.stopLoader('loader-user-validation');
            }
        })
    }

    private userDataHasChanged(): boolean {
        return this.firstNameControl.value != this.user.firstName
            || this.lastNameControl.value != this.user.lastName
            || this.phoneControl.value != this.user.phone;
    }


    private isFormValid(): boolean {
        this.notificationService.clearToastr();

        Object.keys(this.userInfoFormGroup.controls).forEach(field => {
            const control = this.userInfoFormGroup.get(field);
            control?.markAsTouched({ onlySelf: true });
        });

        if (this.userInfoFormGroup.valid)
            return true;

        Object.keys(this.validationMessages).forEach(field => {
            const control = this.userInfoFormGroup.get(field);
            const errorMessages = this.validationMessages[field];

            if (control?.invalid) {
                Object.keys(errorMessages).forEach(errorKey => {
                    if (control.errors?.[errorKey]) {
                        this.notificationService.showErrorToastr(errorMessages[errorKey], { translateMessage: true });
                    }
                });
            }
        });

        return false;
    }
}
